/**
 * Centralized session sanitization for an *invalidated* auth session.
 *
 * ## Why this exists
 *
 * `proxy.ts` admits a request to the app surface (`app.loci.city/<city>/…`) on
 * cookie *presence* alone — the Edge runtime has no JWT decoder. So a browser
 * carrying an expired-but-present `accessToken`/`refreshToken` cookie (the
 * access cookie is 5m/admin·15m/user, the refresh cookie 8h/admin·30d/user)
 * still reaches the feed even though every authenticated API call now 401s.
 *
 * The pre-existing recovery in `AuthContextRTK` redirected to `/`, trusting the
 * proxy to then bounce an anonymous visitor to `/login`. But the proxy treats
 * the still-present stale cookie as authed, so `/` → `/<city>` (the feed) — the
 * user is bounced straight back, never reaching the form. And once the recovery
 * cleared the localStorage session hint, `getCurrentUser` was skipped on every
 * subsequent load (and all other authed queries are gated on the now-null user),
 * so no 401 ever fired again — the recovery could never re-run. The user was
 * permanently stuck on the feed with a signed-out header until they manually
 * cleared all site data (which removes the cookies the proxy was trusting).
 *
 * ## The fix
 *
 * `sanitizeInvalidSession()` is the single, idempotent routine every
 * invalidation path funnels through:
 *
 *   1. Clear the localStorage session hint.
 *   2. Best-effort evict the httpOnly auth cookies. We refresh the CSRF token
 *      first because the `_csrf` cookie's TTL (24h) is far shorter than the
 *      refresh cookie's (up to 30d): an expired `_csrf` would 403 the logout
 *      and leave the auth cookies in place, re-trapping the user. Fetching a
 *      fresh token defeats that without a backend change.
 *   3. On the app surface, hard-redirect to `/login?force=1`. The `?force=1`
 *      escape hatch (FRE-784) makes `proxy.ts` serve the login form regardless
 *      of lingering cookies, so the redirect can't bounce back to the feed even
 *      if eviction raced or failed. On marketing/apex surfaces (public,
 *      ungated) we drop to anonymous without forcing login.
 *
 * ## Why a 401 interceptor, not realtime
 *
 * The most reliable trigger is "the server rejected an authenticated request"
 * (any 401 on the app surface — see `handleUnauthorized`). A realtime
 * "session revoked" push can't cover cookie max-age expiry, JWT-secret
 * rotation, or a tab reopened after the push was sent; a passive 401 interceptor
 * catches all of them with zero extra connections.
 */

import { CITY_EDITIONS } from '@loci/city-editions';

import { resetAnalyticsIdentity } from '../analytics';
import { clearSessionHint } from './sessionHint';

/** LOCI eTLD+1 suffixes (prod + local dev). Mirrors `proxy.ts`. */
const LOCI_HOST_SUFFIXES = ['.loci.city', '.loci.localhost'] as const;
const LOCI_APEX_HOSTS: ReadonlySet<string> = new Set(['loci.city', 'loci.localhost']);

/**
 * Registered city codes, lowercased to match URL path segments. Mirrors the
 * `CITY_CODE_SET` the proxy uses to decide whether an app-surface path is
 * city-scoped.
 */
const CITY_PATH_SEGMENTS: ReadonlySet<string> = new Set(
  Object.keys(CITY_EDITIONS).map((code) => code.toLowerCase()),
);

/**
 * Auth paths where a 401 is expected — never sanitize. `/login` / `/signup`:
 * bad credentials. `/logout` (FRE-1389): the session is being torn down on
 * purpose, and a sanitize here would `location.replace` to
 * `/login?next=/logout…`, beating the post-logout navigation (and turning the
 * next sign-in into another sign-out).
 */
const AUTH_FORM_PATHS: ReadonlySet<string> = new Set(['/login', '/signup', '/logout']);

/**
 * Endpoints whose 401 is an expected auth-flow outcome (wrong password, expired
 * reset token, a capability probe) rather than a stale session. A 401 from any
 * of these must not trigger a forced logout + redirect.
 */
const AUTH_FLOW_ENDPOINTS: ReadonlySet<string> = new Set([
  'login',
  'register',
  'refreshToken',
  'logout',
  'getCurrentUser',
  'getCsrfToken',
  'forgotPassword',
  'resetPassword',
  'verifyEmail',
  'resendVerification',
  'changePassword',
  'passkeyAuthenticationBegin',
  'passkeyAuthenticationFinish',
  'deleteAccount',
  'exportAccountData',
]);

/** Abort the best-effort cookie eviction if it stalls, so the redirect isn't blocked. */
const EVICTION_TIMEOUT_MS = 2500;

/**
 * Single-fire latch. The redirect navigates away (re-instantiating the module),
 * so this naturally resets per page life; the latch only guards against two
 * triggers (the auth-context recovery and a 401 interceptor) racing within one
 * page life and double-firing logout / redirect.
 */
let sanitizeStarted = false;

/**
 * Clear every piece of client-local session residue in one step: the
 * localStorage session hint AND the PostHog person identity (FRE-1261 — it
 * also lives in localStorage and would otherwise survive a hard navigation,
 * leaking the previous account's events and feature-flag buckets on a shared
 * device). Every sign-out/teardown path MUST call this instead of
 * `clearSessionHint()` directly so no future path forgets the identity reset.
 */
export function clearClientSessionResidue(): void {
  clearSessionHint();
  resetAnalyticsIdentity();
}

function isLociHost(hostname: string): boolean {
  return LOCI_APEX_HOSTS.has(hostname) || LOCI_HOST_SUFFIXES.some((s) => hostname.endsWith(s));
}

/**
 * True only for the LOCI app surface (`app.loci.city`, `staging.app.loci.city`,
 * `app.loci.localhost`). A 401 here means `proxy.ts` admitted us on cookie
 * presence but the session is invalid → sanitize. Marketing/apex/`localhost`/
 * preview hosts return false so anonymous browsing is never force-redirected
 * (mirrors the proxy's `fallback`-surface exemption).
 */
export function isAppSurfaceHost(hostname?: string): boolean {
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  if (!host || !isLociHost(host)) return false;
  return /^app\./.test(host) || /^staging\.app\./.test(host);
}

/**
 * True only on a **city-scoped** app-surface page (`app.loci.city/<city>/…`).
 *
 * This mirrors the proxy's auth gate, which is `surface === 'app' && cityCode &&
 * !authed` — the `cityCode` term matters: city-less app paths (`/login`,
 * `/signup`, `/admin`, `/coming-soon`, `/unlock`) resolve `cityCode === null`
 * and the proxy admits them **anonymous, with no cookie**. The forced session
 * bootstrap (Layer-2 stuck-state detection) must use THIS check, not the
 * host-only `isAppSurfaceHost`, or it would fire for a signed-out visitor on
 * `app.loci.city/login`, trip the recovery, and bounce them through
 * `/login?force=1` in an infinite reload loop. The stuck state is always on a
 * city path (`/clt`), so detection is unaffected.
 */
export function isCityScopedAppSurface(hostname?: string, pathname?: string): boolean {
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  if (!isAppSurfaceHost(host)) return false;
  const firstSegment = path.split('/').filter(Boolean)[0]?.toLowerCase() ?? '';
  return CITY_PATH_SEGMENTS.has(firstSegment);
}

function getApiBase(): string {
  const raw = process.env.NEXT_PUBLIC_API_URL;
  return raw ? raw.replace(/\/+$/, '') : '';
}

function readCsrfCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.split('; ').find((c) => c.startsWith('_csrf='));
  if (!match) return undefined;
  const idx = match.indexOf('=');
  return idx >= 0 ? match.slice(idx + 1) : undefined;
}

/**
 * Build the same-origin escape-hatch login URL: `/login?next=<here>&force=1`.
 * Only called on the app surface, where `/login` is same-origin.
 */
function appLoginEscapeUrl(): string {
  const { origin, pathname, search } = window.location;
  const url = new URL('/login', origin);
  const next = `${pathname}${search}`;
  if (next && !next.startsWith('/login')) url.searchParams.set('next', next);
  url.searchParams.set('force', '1');
  return url.toString();
}

/**
 * Best-effort eviction of the httpOnly auth cookies. Refreshes the CSRF token
 * first so an expired `_csrf` cookie can't 403 the logout. Never throws — the
 * `?force=1` redirect unsticks the user even if this fails entirely.
 */
async function evictAuthCookies(): Promise<void> {
  const base = getApiBase();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), EVICTION_TIMEOUT_MS);
  try {
    let csrf = readCsrfCookie();
    try {
      const res = await fetch(`${base}/api/v1/auth/csrf-token`, {
        credentials: 'include',
        signal: controller.signal,
      });
      if (res.ok) {
        const body = (await res.json().catch(() => null)) as { csrfToken?: string } | null;
        csrf = body?.csrfToken ?? readCsrfCookie() ?? csrf;
      }
    } catch {
      // CSRF refresh failed — still attempt logout with whatever token we have.
    }
    await fetch(`${base}/api/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: csrf ? { 'x-csrf-token': csrf } : {},
      signal: controller.signal,
    });
  } catch {
    // Best-effort — the redirect below still unsticks via `?force=1`.
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Sanitize an invalidated session. Idempotent within a page life. SSR-safe.
 *
 * - App surface: clear hint → evict cookies → hard-redirect to `/login?force=1`.
 * - Marketing / apex / fallback: clear hint → evict cookies, then stay
 *   (public surfaces are ungated; the client already renders anonymous).
 */
export async function sanitizeInvalidSession(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (sanitizeStarted) return;
  sanitizeStarted = true;

  clearClientSessionResidue();
  await evictAuthCookies();

  if (isAppSurfaceHost(window.location.hostname)) {
    window.location.replace(appLoginEscapeUrl());
  }
  // Non-app surfaces are public and ungated: clearing the hint + evicting
  // cookies is enough; forcing a marketing visitor to the login form would be
  // a regression.
}

export interface UnauthorizedContext {
  status: number;
  /** RTK Query endpoint name, when resolvable. */
  endpointName?: string;
}

/**
 * Policy for a centrally-observed RTK Query 401 (wired via the store's
 * `rtkQueryErrorMiddleware`). Returns `true` when it owns the response (and has
 * kicked off sanitization), so the caller can suppress the generic
 * "session expired" toast.
 *
 * Only acts when: status is 401, we are on the app surface, the path is not an
 * auth form, and the endpoint is not an auth-flow endpoint. Independent of the
 * localStorage hint — a 401 the proxy admitted to the app surface is a stale
 * session regardless of whether the hint is present.
 */
export function handleUnauthorized({ status, endpointName }: UnauthorizedContext): boolean {
  if (status !== 401) return false;
  if (typeof window === 'undefined') return false;
  if (endpointName && AUTH_FLOW_ENDPOINTS.has(endpointName)) return false;
  if (!isAppSurfaceHost(window.location.hostname)) return false;
  if (AUTH_FORM_PATHS.has(window.location.pathname)) return false;
  void sanitizeInvalidSession();
  return true;
}

/** Test-only: reset the single-fire latch between cases. */
export function resetSessionSanitizeForTests(): void {
  sanitizeStarted = false;
}
